import { computed, inject, Injectable, signal } from '@angular/core';
import { BookingDetailResponse, BookingDetailStation } from '../../models/booking-detail.model';
import { BookingService } from '../booking/booking.service';

/**
 * Discriminated union representing the state of the my-trips detail data.
 *
 * States:
 * - `idle`: Initial state, no data loaded yet
 * - `loading`: Data is being fetched from the API
 * - `ready`: Data has been successfully loaded
 * - `error`: An error occurred while loading data
 */
export type MyTripsDetailState =
  | { status: 'idle'; data: null; error: null }
  | { status: 'loading'; data: null; error: null }
  | { status: 'ready'; data: BookingDetailResponse; error: null }
  | { status: 'error'; data: null; error: string };

/**
 * Type guard to check if the state is ready.
 */
export function isMyTripsDetailReady(
  state: MyTripsDetailState
): state is { status: 'ready'; data: BookingDetailResponse; error: null } {
  return state.status === 'ready';
}

/**
 * Service that manages the state for my-trips detail pages.
 *
 * This service is provided at the route level (`/my-trips/:id`) and shared
 * across all child routes (detail, pickup, dropoff, etc.).
 *
 * The service handles:
 * - Loading booking details from the API
 * - Caching loaded data to avoid redundant API calls
 * - Providing typed signals for components to consume
 *
 * Usage:
 * - Provided via route-level providers in main-layout.routes.ts
 * - Shell component calls `loadBooking(id)` on init
 * - Child pages access data via `booking()` signal
 */
@Injectable()
export class MyTripsDetailStateService {
  private readonly bookingService = inject(BookingService);

  private readonly _bookingId = signal<string | null>(null);
  private readonly _state = signal<MyTripsDetailState>({
    status: 'idle',
    data: null,
    error: null,
  });

  /**
   * Readonly signal exposing the current state.
   */
  public readonly state = this._state.asReadonly();

  /**
   * Computed signal returning the booking data if ready, null otherwise.
   */
  public readonly booking = computed(() => {
    const state = this._state();
    return state.status === 'ready' ? state.data : null;
  });

  /**
   * Computed signal indicating if data is currently loading.
   */
  public readonly isLoading = computed(() => {
    const state = this._state();
    return state.status === 'loading' || state.status === 'idle';
  });

  /**
   * Computed signal returning the error message if in error state.
   */
  public readonly error = computed(() => {
    const state = this._state();
    return state.status === 'error' ? state.error : null;
  });

  /**
   * Computed signal returning the pickup station data.
   */
  public readonly pickupStation = computed((): BookingDetailStation | null => {
    const data = this.booking();
    return data?.booking?.departure ?? null;
  });

  /**
   * Computed signal returning the dropoff station data.
   */
  public readonly dropoffStation = computed((): BookingDetailStation | null => {
    const data = this.booking();
    return data?.booking?.destination ?? null;
  });

  /**
   * Loads booking details from the API.
   *
   * If data for the same booking ID is already loaded, returns immediately.
   * On error, sets error state and re-throws for the caller to handle.
   *
   * @param id - The booking ID to load
   * @throws Error from the API if loading fails
   */
  async loadBooking(id: string): Promise<void> {
    const current = this._state();
    if (current.status === 'ready' && this._bookingId() === id) {
      return;
    }

    this._bookingId.set(id);

    this._state.set({ status: 'loading', data: null, error: null });

    try {
      const data = await this.bookingService.getBookingDetails(id);
      this._state.set({ status: 'ready', data, error: null });
    } catch (err) {
      console.log(err);
      this._state.set({
        status: 'error',
        data: null,
        error: this.handleError(err),
      });
      throw err;
    }
  }

  /**
   * Retries loading with the previously cached booking ID.
   *
   * @throws Error if no booking ID was previously set
   * @throws Error from the API if loading fails
   */
  async retry(): Promise<void> {
    const id = this._bookingId();
    if (!id) {
      throw new Error('No booking ID to retry');
    }

    this._state.set({ status: 'loading', data: null, error: null });

    try {
      const data = await this.bookingService.getBookingDetails(id);
      this._state.set({ status: 'ready', data, error: null });
    } catch (err) {
      this._state.set({
        status: 'error',
        data: null,
        error: this.handleError(err),
      });
      throw err;
    }
  }

  private handleError(err: unknown): string {
    if (err && typeof err === 'object' && 'status' in err) {
      const status = (err as { status: number }).status;
      if (status === 400) return 'Ungültige Buchungs-ID';
      if (status === 404) return 'Buchung nicht gefunden';
      if (status === 500) return 'Serverfehler - Bitte später erneut versuchen';
      if (status === 0) return 'Verbindungsfehler. Prüfen Sie Ihre Internetverbindung.';
    }
    return 'Ein unerwarteter Fehler ist aufgetreten';
  }

  /**
   * Forces a refresh of the current booking data from the API.
   *
   * Unlike loadBooking(), this method bypasses the cache and always
   * fetches fresh data. Use this when you know the booking has been
   * updated externally (e.g., after vehicle inspection completion).
   *
   * @throws Error if no booking ID was previously set
   * @throws Error from the API if loading fails
   */
  async refreshBooking(): Promise<void> {
    const id = this._bookingId();

    if (!id) {
      throw new Error('No booking ID to refresh');
    }

    this._state.set({ status: 'loading', data: null, error: null });

    try {
      const data = await this.bookingService.getBookingDetails(id);
      this._state.set({ status: 'ready', data, error: null });
    } catch (err) {
      this._state.set({
        status: 'error',
        data: null,
        error: this.handleError(err),
      });

      throw err;
    }
  }
}
